import React, { useState, useEffect } from "react";
import "@/App.css";
import { translations } from "./translations";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { 
  Search, 
  BarChart3, 
  Settings, 
  Gavel,
  ChevronDown,
  Menu,
  X,
  ArrowRight,
  Check,
  Shield,
  FileText,
  Users,
  Building,
  TrendingUp,
  Phone,
  Mail,
  MapPin,
  Globe,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo components
const LogoGreen = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_60c4b816-5cbf-4932-bfa8-df701b6b40cb/artifacts/wqqij0d2_Logo_Edstate-01.png" 
    alt="EDSTATE" 
    className="h-10 w-auto"
  />
);

const LogoWhite = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_60c4b816-5cbf-4932-bfa8-df701b6b40cb/artifacts/v7hb0dtq_Logo_Edstate-03.png" 
    alt="EDSTATE" 
    className="h-10 w-auto"
  />
);

// Navigation Component
const Navigation = ({ lang, setLang, t }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle scroll to section after navigation
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scrollTo = params.get('scrollTo');
    if (scrollTo && isHomePage) {
      setTimeout(() => {
        const element = document.getElementById(scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
        // Clean up URL
        navigate('/', { replace: true });
      }, 100);
    }
  }, [location, isHomePage, navigate]);

  const navLinks = [
    { href: "about", label: t.nav.about },
    { href: "services", label: t.nav.services },
    { href: "process", label: t.nav.process },
    { href: "investment", label: t.nav.investment },
    { href: "contact", label: t.nav.contact },
  ];

  const languages = [
    { code: "es", label: "ES" },
    { code: "en", label: "EN" },
    { code: "pt", label: "PT" },
  ];

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    if (isHomePage) {
      // Already on home, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home with scroll target
      navigate(`/?scrollTo=${sectionId}`);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-lg" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            onClick={handleLogoClick}
            className="flex-shrink-0" 
            data-testid="logo-link"
          >
            <LogoGreen />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={`/#${link.href}`}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium text-[#06302F] hover:text-[#007258] transition-colors duration-200 tracking-wide"
                data-testid={`nav-link-${link.href}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side - Language + CTA */}
          <div className="hidden lg:flex items-center gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 text-[#06302F] hover:text-[#007258] hover:bg-transparent"
                  data-testid="language-selector"
                >
                  <Globe className="w-4 h-4" />
                  <span className="uppercase font-medium">{lang}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[80px]">
                {languages.map((language) => (
                  <DropdownMenuItem
                    key={language.code}
                    onClick={() => setLang(language.code)}
                    className={`cursor-pointer ${lang === language.code ? "bg-[#007258]/10 text-[#007258]" : ""}`}
                    data-testid={`lang-option-${language.code}`}
                  >
                    {language.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a 
              href="/#contact"
              onClick={(e) => handleNavClick(e, 'contact')}
            >
              <Button 
                className="btn-primary text-white px-6 py-5 text-xs uppercase tracking-widest font-medium rounded-none"
                data-testid="nav-cta-button"
              >
                {t.hero.cta}
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-[#06302F]"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-white"
              data-testid="mobile-menu"
            >
              <div className="py-4 space-y-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={`/#${link.href}`}
                    onClick={(e) => {
                      handleNavClick(e, link.href);
                      setMobileMenuOpen(false);
                    }}
                    className="block py-2 text-[#06302F] hover:text-[#007258] font-medium"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="flex gap-4 pt-4 border-t">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => {
                        setLang(language.code);
                        setMobileMenuOpen(false);
                      }}
                      className={`px-3 py-1 text-sm font-medium ${
                        lang === language.code
                          ? "bg-[#007258] text-white"
                          : "text-[#06302F] border border-gray-200"
                      }`}
                    >
                      {language.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = ({ t }) => (
  <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
    {/* Background Image */}
    <div className="absolute inset-0 z-0">
      <img
        src="https://images.unsplash.com/photo-1770018896224-4701c315c9d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjB2aWxsYSUyMG1hZGVpcmElMjBwb3J0dWdhbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzA5MTc2MjN8MA&ixlib=rb-4.1.0&q=85"
        alt="Luxury villa in Madeira"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#06302F]/90 via-[#06302F]/70 to-transparent" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40">
      <div className="max-w-2xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[#55AF88] text-xs uppercase tracking-[0.3em] font-medium mb-6"
          data-testid="hero-tagline"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6"
          data-testid="hero-title"
        >
          {t.hero.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg text-white/80 leading-relaxed mb-10 max-w-xl"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <a href="#contact">
            <Button 
              className="btn-primary text-white px-8 py-6 text-sm uppercase tracking-widest font-medium rounded-none w-full sm:w-auto"
              data-testid="hero-cta-primary"
            >
              {t.hero.cta}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
          <a href="#about">
            <Button 
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-sm uppercase tracking-widest font-medium rounded-none w-full sm:w-auto"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </Button>
          </a>
        </motion.div>
      </div>
    </div>

    {/* Scroll indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      className="absolute bottom-10 left-1/2 -translate-x-1/2 scroll-indicator"
    >
      <ChevronDown className="w-8 h-8 text-white/60" />
    </motion.div>
  </section>
);

// About Section
const AboutSection = ({ t }) => (
  <section id="about" className="py-20 md:py-32 bg-white" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative img-zoom"
        >
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_madeira-invest/artifacts/qjce1u1l_realistic-photo-of-a-house-under-construction--par.png"
              alt="Proyecto inmobiliario en Madeira"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Decorative element */}
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#007258] to-[#55AF88] -z-10" />
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#007258] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            {t.about.label}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#06302F] tracking-tight mb-8 leading-tight">
            {t.about.title}
          </h2>
          
          <div className="space-y-6 text-[#06302F]/70 leading-relaxed">
            <p>{t.about.p1}</p>
            <p><span className="font-bold text-[#007258]">{t.about.p2Bold}</span> {t.about.p2}</p>
            <p>{t.about.p3}</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-gray-100">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-[#007258] stat-number">{t.about.stat1}</p>
              <p className="text-sm text-[#06302F]/60 mt-1">{t.about.stat1Label}</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-[#007258] stat-number">{t.about.stat2}</p>
              <p className="text-sm text-[#06302F]/60 mt-1">{t.about.stat2Label}</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-[#007258] stat-number">{t.about.stat3}</p>
              <p className="text-sm text-[#06302F]/60 mt-1">{t.about.stat3Label}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = ({ t }) => {
  const services = [
    { icon: Search, title: t.services.s1Title, desc: t.services.s1Desc },
    { icon: BarChart3, title: t.services.s2Title, desc: t.services.s2Desc },
    { icon: Settings, title: t.services.s3Title, desc: t.services.s3Desc },
    { icon: Gavel, title: t.services.s4Title, desc: t.services.s4Desc },
  ];

  return (
    <section id="services" className="py-20 md:py-32 bg-[#F4F4F5]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-[#007258] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            {t.services.label}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#06302F] tracking-tight">
            {t.services.title}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="service-card bg-white border border-gray-100 p-8 md:p-10"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#007258] to-[#55AF88] mb-6">
                <service.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#06302F] mb-4">{service.title}</h3>
              <p className="text-[#06302F]/70 leading-relaxed">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = ({ t }) => {
  const steps = [
    { num: "01", title: t.process.step1, desc: t.process.step1Desc },
    { num: "02", title: t.process.step2, desc: t.process.step2Desc },
    { num: "03", title: t.process.step3, desc: t.process.step3Desc },
    { num: "04", title: t.process.step4, desc: t.process.step4Desc },
    { num: "05", title: t.process.step5, desc: t.process.step5Desc },
    { num: "06", title: t.process.step6, desc: t.process.step6Desc },
    { num: "07", title: t.process.step7, desc: t.process.step7Desc },
  ];

  return (
    <section id="process" className="py-20 md:py-32 bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-[#007258] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            {t.process.label}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#06302F] tracking-tight">
            {t.process.title}
          </h2>
        </motion.div>

        <div className="relative">
          {/* Timeline line - hidden on mobile */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#007258] to-[#55AF88] -translate-x-1/2" />

          <div className="space-y-12 md:space-y-0">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative md:flex items-center ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
                data-testid={`process-step-${index}`}
              >
                {/* Content */}
                <div className={`md:w-1/2 ${index % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16"}`}>
                  <div className="bg-white p-6 md:p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <span className="text-4xl font-bold text-[#007258]/20">{step.num}</span>
                    <h3 className="text-xl font-bold text-[#06302F] mt-2 mb-3">{step.title}</h3>
                    <p className="text-[#06302F]/70 leading-relaxed">{step.desc}</p>
                  </div>
                </div>

                {/* Timeline dot - hidden on mobile */}
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#007258] border-4 border-white shadow-lg z-10" />

                {/* Empty space for alternating layout */}
                <div className="hidden md:block md:w-1/2" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Investment Models Section
const InvestmentSection = ({ t }) => {
  const sliders = [
    { label: t.investment.slider1Label, left: t.investment.slider1Left, right: t.investment.slider1Right, model1Pos: 15, model2Pos: 75 },
    { label: t.investment.slider2Label, left: t.investment.slider2Left, right: t.investment.slider2Right, model1Pos: 90, model2Pos: 40 },
    { label: t.investment.slider3Label, left: t.investment.slider3Left, right: t.investment.slider3Right, model1Pos: 10, model2Pos: 60 },
  ];

  return (
    <section id="investment" className="py-20 md:py-32 bg-[#06302F]" data-testid="investment-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-[#55AF88] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            {t.investment.label}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            {t.investment.title}
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t.investment.subtitle}
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16">
          {/* Model 1 - Full Management */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white p-8 md:p-10 relative overflow-hidden"
            data-testid="investment-model-1"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#007258] to-[#55AF88]" />
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#007258]">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#06302F]">{t.investment.model1}</h3>
            </div>
            <p className="text-[#06302F]/60 mb-6 text-sm">{t.investment.model1Desc}</p>
            
            <ul className="space-y-3">
              {t.investment.model1Features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#007258] flex-shrink-0 mt-1" />
                  <span className="text-[#06302F]/80 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Model 2 - Shared Management */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white p-8 md:p-10 relative overflow-hidden"
            data-testid="investment-model-2"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#55AF88] to-[#007258]" />
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#55AF88]">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#06302F]">{t.investment.model2}</h3>
            </div>
            <p className="text-[#06302F]/60 mb-6 text-sm">{t.investment.model2Desc}</p>
            
            <ul className="space-y-3">
              {t.investment.model2Features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#55AF88] flex-shrink-0 mt-1" />
                  <span className="text-[#06302F]/80 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Visual Comparison Sliders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-10 mb-16"
        >
          <div className="grid md:grid-cols-3 gap-8">
            {sliders.map((slider, index) => (
              <div key={index} className="space-y-4">
                <p className="text-white/80 text-sm font-medium text-center">{slider.label}</p>
                <div className="relative">
                  {/* Track */}
                  <div className="h-2 bg-white/20 rounded-full relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#007258] to-[#55AF88] rounded-full opacity-30" />
                  </div>
                  {/* Labels */}
                  <div className="flex justify-between mt-2">
                    <span className="text-white/50 text-xs">{slider.left}</span>
                    <span className="text-white/50 text-xs">{slider.right}</span>
                  </div>
                  {/* Markers */}
                  <div 
                    className="absolute top-0 w-4 h-4 bg-[#007258] rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1"
                    style={{ left: `${slider.model1Pos}%` }}
                    title="Gestión Integral"
                  />
                  <div 
                    className="absolute top-0 w-4 h-4 bg-[#55AF88] rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1"
                    style={{ left: `${slider.model2Pos}%` }}
                    title="Gestión Compartida"
                  />
                </div>
              </div>
            ))}
          </div>
          {/* Legend */}
          <div className="flex justify-center gap-8 mt-8 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#007258] rounded-full" />
              <span className="text-white/60 text-xs">{t.investment.model1}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#55AF88] rounded-full" />
              <span className="text-white/60 text-xs">{t.investment.model2}</span>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center"
        >
          <h3 className="text-2xl font-bold text-white mb-4">{t.investment.ctaTitle}</h3>
          <p className="text-white/70 max-w-2xl mx-auto mb-6">
            {t.investment.ctaText}
          </p>
          <a href="#contact">
            <Button 
              className="bg-white text-[#06302F] hover:bg-white/90 px-8 py-6 text-sm uppercase tracking-widest font-medium rounded-none"
              data-testid="investment-cta-button"
            >
              {t.investment.ctaButton}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
          <p className="text-white/40 text-xs mt-6 flex items-center justify-center gap-2">
            <Shield className="w-3 h-3" />
            {t.investment.ctaConfidential}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = ({ t }) => {
  const benefits = [
    { icon: Search, text: t.benefits.b1 },
    { icon: TrendingUp, text: t.benefits.b2 },
    { icon: Settings, text: t.benefits.b3 },
    { icon: Users, text: t.benefits.b4 },
    { icon: Shield, text: t.benefits.b5 },
    { icon: Building, text: t.benefits.b6 },
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-[#007258] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            {t.benefits.label}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#06302F] tracking-tight">
            {t.benefits.title}
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="flex items-start gap-4 p-6 bg-[#F4F4F5] hover:bg-[#007258]/5 transition-colors duration-300"
              data-testid={`benefit-item-${index}`}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-[#007258] flex-shrink-0">
                <benefit.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-[#06302F]/80 leading-relaxed">{benefit.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Security Section
const SecuritySection = ({ t }) => {
  const guarantees = [
    { icon: Shield, title: t.security.s1Title, desc: t.security.s1Desc, checkpoint: t.security.checkpoint1 },
    { icon: Users, title: t.security.s2Title, desc: t.security.s2Desc, checkpoint: t.security.checkpoint2 },
    { icon: FileText, title: t.security.s3Title, desc: t.security.s3Desc, checkpoint: t.security.checkpoint3 },
    { icon: Building, title: t.security.s4Title, desc: t.security.s4Desc, checkpoint: t.security.checkpoint4 },
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="security-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-[#007258] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            {t.security.label}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#06302F] tracking-tight mb-4">
            {t.security.title}
          </h2>
          <p className="text-[#06302F]/60 max-w-2xl mx-auto">
            {t.security.subtitle}
          </p>
        </motion.div>

        {/* Progress Line with Checkpoints */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-16 hidden md:block"
        >
          <div className="relative flex items-center justify-between max-w-3xl mx-auto">
            {/* Line */}
            <div className="absolute left-0 right-0 h-0.5 bg-gray-200 top-1/2 -translate-y-1/2" />
            <motion.div 
              className="absolute left-0 h-0.5 bg-gradient-to-r from-[#007258] to-[#55AF88] top-1/2 -translate-y-1/2"
              initial={{ width: "0%" }}
              whileInView={{ width: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }}
            />
            
            {/* Checkpoints */}
            {guarantees.map((item, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.5 + index * 0.2 }}
                className="relative z-10 flex flex-col items-center"
              >
                <div className="w-10 h-10 rounded-full bg-white border-2 border-[#007258] flex items-center justify-center shadow-sm">
                  <Check className="w-5 h-5 text-[#007258]" />
                </div>
                <span className="mt-3 text-xs font-medium text-[#06302F]/70 uppercase tracking-wider">
                  {item.checkpoint}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {guarantees.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group bg-[#F4F4F5] p-6 border border-transparent hover:border-[#007258]/20 hover:bg-white hover:shadow-lg transition-all duration-300 cursor-default"
              data-testid={`security-item-${index}`}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-[#007258] group-hover:bg-gradient-to-br group-hover:from-[#007258] group-hover:to-[#55AF88] transition-all duration-300 mb-5">
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#06302F] mb-2 group-hover:text-[#007258] transition-colors duration-300">
                {item.title}
              </h3>
              <p className="text-sm text-[#06302F]/60 leading-relaxed">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ t, lang }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentError, setConsentError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate consent
    if (!formData.consent) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language: lang,
      });
      
      toast.success(t.contact.success);
      setFormData({ name: "", email: "", phone: "", message: "", consent: false });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(t.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#007258] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
              {t.contact.label}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#06302F] tracking-tight mb-6 leading-tight">
              {t.contact.title}
            </h2>
            <p className="text-[#06302F]/70 leading-relaxed mb-10">
              {t.contact.subtitle}
            </p>

            {/* Contact info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#007258]">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-[#06302F]/60">Email</p>
                  <p className="text-[#06302F] font-medium">info@edstatecapital.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#007258]">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-[#06302F]/60">Phone</p>
                  <p className="text-[#06302F] font-medium">+351 961 568 398</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#007258]">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-[#06302F]/60">Location</p>
                  <p className="text-[#06302F] font-medium">Funchal, Madeira, Portugal</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Input
                  type="text"
                  placeholder={t.contact.name}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-none border-gray-200 focus:border-[#007258] focus:ring-[#007258] bg-gray-50/50 py-6"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder={t.contact.email}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="rounded-none border-gray-200 focus:border-[#007258] focus:ring-[#007258] bg-gray-50/50 py-6"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  placeholder={t.contact.phone}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-none border-gray-200 focus:border-[#007258] focus:ring-[#007258] bg-gray-50/50 py-6"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Textarea
                  placeholder={t.contact.messagePlaceholder}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="rounded-none border-gray-200 focus:border-[#007258] focus:ring-[#007258] bg-gray-50/50 resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              
              {/* GDPR Consent Checkbox */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) => {
                      setFormData({ ...formData, consent: e.target.checked });
                      if (e.target.checked) setConsentError(false);
                    }}
                    className="mt-1 w-4 h-4 rounded-none border-gray-300 text-[#007258] focus:ring-[#007258] cursor-pointer"
                    data-testid="contact-consent-checkbox"
                  />
                  <span className="text-sm text-[#06302F]/70 group-hover:text-[#06302F] transition-colors">
                    {t.contact.consent}{" "}
                    <Link 
                      to="/privacy" 
                      className="text-[#007258] hover:text-[#06302F] underline font-medium transition-colors"
                      target="_blank"
                    >
                      {t.contact.consentLink}
                    </Link>
                    .
                  </span>
                </label>
                {consentError && (
                  <p className="text-red-500 text-sm flex items-center gap-2" data-testid="consent-error">
                    <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                    {t.contact.consentError}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary text-white w-full py-6 text-sm uppercase tracking-widest font-medium rounded-none"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="spinner" />
                    {t.contact.sending}
                  </span>
                ) : (
                  <>
                    {t.contact.submit}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ t }) => (
  <footer className="bg-[#06302F] py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <div className="flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="flex flex-col items-center md:items-start gap-4">
          <Link to="/">
            <LogoWhite />
          </Link>
          <p className="text-white/60 text-sm">{t.footer.tagline}</p>
        </div>
        
        <div className="flex flex-col items-center md:items-end gap-4">
          <div className="flex gap-6">
            <Link to="/privacy" className="text-white/60 hover:text-white text-sm transition-colors duration-200">
              {t.footer.privacy}
            </Link>
            <Link to="/terms" className="text-white/60 hover:text-white text-sm transition-colors duration-200">
              {t.footer.terms}
            </Link>
          </div>
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} EDSTATE. {t.footer.rights}.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Legal Section Component
const LegalSection = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-[#06302F] mb-4 pb-2 border-b border-gray-200">
      {title}
    </h2>
    <div className="text-[#06302F]/70 leading-relaxed space-y-4">
      {children}
    </div>
  </div>
);

// Privacy Page
const PrivacyPage = ({ t, lang, setLang }) => {
  const currentDate = "2025-02-20";
  const s = t.privacy.sections;
  
  return (
    <div className="min-h-screen bg-white">
      <Navigation lang={lang} setLang={setLang} t={t} />
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#007258] hover:text-[#06302F] mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">{t.privacy.backButton}</span>
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#06302F] tracking-tight mb-4">
              {t.privacy.title}
            </h1>
            
            <p className="text-[#06302F]/50 text-sm mb-12">
              {t.privacy.lastUpdate}: {currentDate}
            </p>
            
            <div className="prose prose-lg max-w-none">
              {/* Introducción */}
              <LegalSection title={s.intro.title}>
                <p>{s.intro.content}</p>
              </LegalSection>

              {/* Responsable */}
              <LegalSection title={s.controller.title}>
                <p>{s.controller.content}</p>
                <div className="bg-[#F4F4F5] p-4 rounded mt-2">
                  <p className="font-medium text-[#06302F]">{s.controller.name}</p>
                  <p>{s.controller.location}</p>
                  <p>{s.controller.email}</p>
                </div>
              </LegalSection>

              {/* Datos recopilados */}
              <LegalSection title={s.dataCollected.title}>
                <p>{s.dataCollected.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.dataCollected.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm italic mt-4 bg-[#007258]/5 p-3 rounded">{s.dataCollected.note}</p>
              </LegalSection>

              {/* Finalidad */}
              <LegalSection title={s.purpose.title}>
                <p>{s.purpose.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.purpose.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </LegalSection>

              {/* Base legal */}
              <LegalSection title={s.legalBasis.title}>
                <p>{s.legalBasis.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.legalBasis.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </LegalSection>

              {/* Conservación */}
              <LegalSection title={s.retention.title}>
                <p>{s.retention.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.retention.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </LegalSection>

              {/* Destinatarios */}
              <LegalSection title={s.recipients.title}>
                <p>{s.recipients.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.recipients.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm mt-4">{s.recipients.note}</p>
              </LegalSection>

              {/* Transferencias */}
              <LegalSection title={s.transfers.title}>
                <p>{s.transfers.content}</p>
              </LegalSection>

              {/* Derechos */}
              <LegalSection title={s.rights.title}>
                <p>{s.rights.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.rights.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="mt-4">{s.rights.exercise}</p>
                <p className="mt-2">{s.rights.complaint}</p>
              </LegalSection>

              {/* Seguridad */}
              <LegalSection title={s.security.title}>
                <p>{s.security.content}</p>
              </LegalSection>

              {/* Cambios */}
              <LegalSection title={s.changes.title}>
                <p>{s.changes.content}</p>
              </LegalSection>

              {/* Contacto */}
              <LegalSection title={s.contact.title}>
                <p>{s.contact.content}</p>
                <a 
                  href={`mailto:${s.contact.email}`}
                  className="text-[#007258] hover:text-[#06302F] font-medium transition-colors"
                >
                  {s.contact.email}
                </a>
              </LegalSection>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer t={t} />
    </div>
  );
};

// Terms Page
const TermsPage = ({ t, lang, setLang }) => {
  const currentDate = "2025-02-20";
  const s = t.terms.sections;
  
  return (
    <div className="min-h-screen bg-white">
      <Navigation lang={lang} setLang={setLang} t={t} />
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#007258] hover:text-[#06302F] mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">{t.terms.backButton}</span>
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#06302F] tracking-tight mb-4">
              {t.terms.title}
            </h1>
            
            <p className="text-[#06302F]/50 text-sm mb-12">
              {t.terms.lastUpdate}: {currentDate}
            </p>
            
            <div className="prose prose-lg max-w-none">
              {/* Introducción */}
              <LegalSection title={s.intro.title}>
                <p>{s.intro.content}</p>
              </LegalSection>

              {/* Finalidad */}
              <LegalSection title={s.purpose.title}>
                <p>{s.purpose.content}</p>
              </LegalSection>

              {/* Exención */}
              <LegalSection title={s.disclaimer.title}>
                <p>{s.disclaimer.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.disclaimer.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="mt-4 bg-[#007258]/5 p-4 rounded text-sm font-medium text-[#06302F]">
                  {s.disclaimer.note}
                </p>
              </LegalSection>

              {/* Limitación */}
              <LegalSection title={s.liability.title}>
                <p>{s.liability.content}</p>
                <ul className="list-disc pl-6 space-y-1">
                  {s.liability.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </LegalSection>

              {/* Propiedad intelectual */}
              <LegalSection title={s.ip.title}>
                <p>{s.ip.content}</p>
                <p className="mt-2 font-medium">{s.ip.restrictions}</p>
              </LegalSection>

              {/* Enlaces */}
              <LegalSection title={s.links.title}>
                <p>{s.links.content}</p>
              </LegalSection>

              {/* Modificaciones */}
              <LegalSection title={s.modifications.title}>
                <p>{s.modifications.content}</p>
              </LegalSection>

              {/* Ley aplicable */}
              <LegalSection title={s.law.title}>
                <p>{s.law.content}</p>
              </LegalSection>

              {/* Contacto */}
              <LegalSection title={s.contact.title}>
                <p>{s.contact.content}</p>
                <a 
                  href={`mailto:${s.contact.email}`}
                  className="text-[#007258] hover:text-[#06302F] font-medium transition-colors"
                >
                  {s.contact.email}
                </a>
              </LegalSection>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer t={t} />
    </div>
  );
};

// Home Page (Landing)
const HomePage = ({ t, lang, setLang }) => (
  <>
    <Navigation lang={lang} setLang={setLang} t={t} />
    <HeroSection t={t} />
    <AboutSection t={t} />
    <ServicesSection t={t} />
    <ProcessSection t={t} />
    <InvestmentSection t={t} />
    <SecuritySection t={t} />
    <ContactSection t={t} lang={lang} />
    <Footer t={t} />
  </>
);

// Main App Component
function App() {
  // Detect browser language and set default
  const getDefaultLanguage = () => {
    const browserLang = navigator.language?.toLowerCase() || 'pt';
    if (browserLang.startsWith('es')) return 'es';
    if (browserLang.startsWith('en')) return 'en';
    if (browserLang.startsWith('pt')) return 'pt';
    return 'pt'; // Default to Portuguese
  };

  const [lang, setLang] = useState(getDefaultLanguage);
  const t = translations[lang];

  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage t={t} lang={lang} setLang={setLang} />} />
          <Route path="/privacy" element={<PrivacyPage t={t} lang={lang} setLang={setLang} />} />
          <Route path="/terms" element={<TermsPage t={t} lang={lang} setLang={setLang} />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
